import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import { PLACEMENT_STATUS_LABELS, trackFeaturedPlacementEvent } from "../../../constants/automapFeaturedPlacementConstants";
import {
  cancelPlacement,
  createPlacement,
  fetchListingPlacements,
  pausePlacement,
} from "../../../services/automapFeaturedPlacementApi";
import { AdminPlacementsNav, PlacementForm, toIsoFromLocalInput } from "./AdminPlacementsShared";

const DEFAULT_FORM = {
  placementType: "search_top_slot",
  dealerId: "",
  listingId: "",
  city: "",
  startAtLocal: "",
  endAtLocal: "",
  priority: 50,
  adminOverride: false,
  overrideReason: "",
  force: false,
  notes: "",
};

export default function AdminListingPlacementsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchListingPlacements();
      setRows(data.placements || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      await createPlacement({
        placementType: form.placementType,
        dealerId: form.dealerId,
        listingId: form.listingId || null,
        city: form.city || null,
        startAt: toIsoFromLocalInput(form.startAtLocal),
        endAt: toIsoFromLocalInput(form.endAtLocal),
        priority: Number(form.priority) || 50,
        adminOverride: form.adminOverride,
        overrideReason: form.overrideReason,
        force: form.force,
        notes: form.notes,
      });
      trackFeaturedPlacementEvent("admin_placement_created");
      setForm(DEFAULT_FORM);
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader eyebrow="AutoMapa · Destacados" title="Placements de listings" description="Programar vehículos destacados en búsqueda, home y SEO." />
      <AdminAutomotiveNav active="placements" />
      <AdminPlacementsNav active="listings" />
      <main className="admin-console__main wrap">
        <PlacementForm form={form} onChange={setForm} onSubmit={submit} submitting={submitting} error={formError} />
        {loading ? <p className="dealer-state-box">Cargando…</p> : null}
        {error ? <p className="dealer-state-box">{error}</p> : null}
        {!loading ? (
          <div className="import-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Listing</th>
                  <th>Dealer</th>
                  <th>Slot</th>
                  <th>Estado</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th>Elegible</th>
                  <th>Imp.</th>
                  <th>Clics</th>
                  <th>Leads</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.listingTitle || row.listingId || "—"}</td>
                    <td>{row.dealerName}</td>
                    <td>{row.slotKey}</td>
                    <td>{PLACEMENT_STATUS_LABELS[row.runtimeStatus] || row.runtimeStatus}</td>
                    <td>{row.startAt?.slice(0, 10)}</td>
                    <td>{row.endAt?.slice(0, 10)}</td>
                    <td>{row.eligibilityOk ? "Sí" : "No"}</td>
                    <td>{row.performance?.impressions ?? 0}</td>
                    <td>{row.performance?.clicks ?? 0}</td>
                    <td>{row.performance?.leads ?? 0}</td>
                    <td>
                      <Link to={`/admin/listings/${row.listingId}`}>Listing</Link>
                      {" · "}
                      <button type="button" onClick={() => pausePlacement(row.id).then(load)}>Pausar</button>
                      {" · "}
                      <button type="button" onClick={() => cancelPlacement(row.id, "Cancelado admin").then(load)}>Cancelar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </main>
    </AdminPageLayout>
  );
}
